import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaBullhorn, FaCheck } from "react-icons/fa";
import { apihubGet, apihubPost } from "../../api/apihub";
import { businessPath } from "../../config/businessMenu";
import { allowDevMockFallback } from "../../utils/devMockFallback";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { getUserName } from "../../utils/auth";
import { isApprovedTemplate } from "../../utils/templatePreview";
import CampaignTemplatePicker from "./CampaignTemplatePicker";

const STEPS = [
  "Campaign Name",
  "Select Audience",
  "Choose Template",
  "Schedule & Launch",
];

const LAST_SEEN_OPTIONS = [
  { value: "", label: "Any" },
  { value: "24h", label: "24 Hours" },
  { value: "7d", label: "7 Days" },
  { value: "30d", label: "30 Days" },
];

const MOCK_TEMPLATES = [
  {
    _id: "69f6ffb2b1ae76dbff45c051",
    template_id: "4513437452222207",
    name: "democonfirmation_classopt",
    category: "MARKETING",
    language: "en",
    template_status: "APPROVED",
  },
];

const BroadcastCampaign = () => {
  const { projectId, phoneNoId, wabaId } = useProjectPhone();

  const [step, setStep] = useState(0);
  const [campaignName, setCampaignName] = useState("");

  const [lastSeen, setLastSeen] = useState("");
  const [createdAtFilter, setCreatedAtFilter] = useState("");
  const [optedIn, setOptedIn] = useState("all");
  const [incomingBlocked, setIncomingBlocked] = useState("");

  const [customers, setCustomers] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [successId, setSuccessId] = useState("");
  const [error, setError] = useState("");

  const fetchTemplates = useCallback(async () => {
    if (!phoneNoId) return;
    setLoadingTemplates(true);
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/gettotaltemplatesbyphonenumberid/${phoneNoId}?page=1&limit=100&sortBy=createdAt&sortOrder=desc`,
      );
      const list = res?.data || res?.templates || [];
      const next =
        Array.isArray(list) && list.length
          ? list
          : allowDevMockFallback()
            ? MOCK_TEMPLATES
            : [];
      setTemplates(next);
      setSelectedTemplate((prev) =>
        prev && isApprovedTemplate(prev) ? prev : null,
      );
    } catch {
      setTemplates(allowDevMockFallback() ? MOCK_TEMPLATES : []);
      setSelectedTemplate(null);
    } finally {
      setLoadingTemplates(false);
    }
  }, [phoneNoId]);

  const applyAudienceFilter = useCallback(async () => {
    if (!phoneNoId) return;
    setLoadingCustomers(true);
    setError("");
    const params = new URLSearchParams({ page: "1", limit: "50" });
    if (lastSeen) params.set("lastSeen", lastSeen);
    if (createdAtFilter) params.set("createdAt", createdAtFilter);
    if (optedIn !== "all") params.set("optedIn", optedIn);
    if (incomingBlocked !== "") {
      params.set("incomingBlocked", incomingBlocked);
    }

    try {
      const res = await apihubPost(
        `/getInteractedCustomerDetail/getfilteredcontacts/${phoneNoId}?${params}`,
        { filters: [] },
      );
      const list = (res?.customers || []).filter(
        (c) => c.status === "inactive" || !c.status,
      );
      setCustomers(list);
      setTotalCustomers(Number(res?.totalCustomers) || list.length);
      setSelectedIds(new Set());
    } catch (err) {
      setCustomers([]);
      setTotalCustomers(0);
      setError(err.message || "Could not load audience");
    } finally {
      setLoadingCustomers(false);
    }
  }, [phoneNoId, lastSeen, createdAtFilter, optedIn, incomingBlocked]);

  useEffect(() => {
    if (step === 2) fetchTemplates();
  }, [step, fetchTemplates]);

  const toggleCustomer = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllCustomers = () => {
    if (selectedIds.size === customers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(customers.map((c) => c._id)));
    }
  };

  const canNext = () => {
    if (step === 0) return campaignName.trim().length > 0;
    if (step === 1) return selectedIds.size > 0;
    if (step === 2) return Boolean(selectedTemplate);
    return true;
  };

  const handleLaunch = async () => {
    if (!selectedTemplate || selectedIds.size === 0) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        campaignName: campaignName.trim(),
        campaigntype: "Broadcast",
        selectedContacts: [...selectedIds],
        selectedTemplate,
        currentPhoneNumberId: phoneNoId,
        currentWabaId: wabaId || phoneNoId,
        headers: {},
        templatemediaurl: "",
        bodyValues: {},
        headerValues: {},
        buttonValues: {},
        templatetypelatitude: "",
        templatetypelongtitude: "",
        templatetypelocationname: "",
        templatetypelocationaddress: "",
        carouselcardmediaurl: {},
        carouselcardplaceholdervalues: {},
        isScheduled: scheduleOn ? "On" : "Off",
        selectedDate: scheduleOn ? scheduleDate : "",
        selectedTime: scheduleOn ? scheduleTime : "",
        senderUserName: getUserName() || "Admin",
        projectId,
      };

      const res = await apihubPost(
        "/getInteractedCustomerDetail/sendingnormalbroadcast",
        payload,
      );
      setSuccessId(res?.campaignid || res?.data?._id || "ok");
    } catch (err) {
      setError(
        err.message ||
          "Launch failed. Sign in to apihub with a valid token to send broadcasts.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const clearFilters = () => {
    setLastSeen("");
    setCreatedAtFilter("");
    setOptedIn("all");
    setIncomingBlocked("");
    setCustomers([]);
    setTotalCustomers(0);
    setSelectedIds(new Set());
  };

  return (
    <div className="biz-panel-page biz-broadcast-page">
      <div className="biz-panel-header">
        <h1>
          <FaBullhorn />
          Broadcast Campaign
        </h1>
      </div>

      <div className="biz-broadcast-steps">
        {STEPS.map((label, idx) => (
          <div
            key={label}
            className={`biz-broadcast-step ${
              idx === step ? "active" : idx < step ? "done" : ""
            }`}
          >
            <span className="biz-broadcast-step-num">
              {idx < step ? <FaCheck /> : idx + 1}
            </span>
            <span className="biz-broadcast-step-label">{label}</span>
          </div>
        ))}
      </div>

      <div className="biz-broadcast-body">
        {successId ? (
          <div className="biz-broadcast-success">
            <h2>Campaign launched</h2>
            <p>
              Your broadcast &quot;{campaignName}&quot; was submitted to{" "}
              {selectedIds.size} contact(s).
            </p>
            <Link
              to={businessPath(projectId, "fetchingallcampaign")}
              className="biz-btn-primary"
            >
              View Campaign Report
            </Link>
          </div>
        ) : (
          <>
            {step === 0 && (
              <div className="biz-broadcast-hero">
                <h2>Campaign Name</h2>
                <p>Pick something that describes your audience &amp; goals.</p>
                <input
                  type="text"
                  placeholder="Enter Campaign Name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                />
              </div>
            )}

            {step === 1 && (
              <div className="biz-broadcast-audience">
                <div className="biz-broadcast-section-head">
                  <h2>Select Audience</h2>
                  <p>Pick your target group audience to follow up with.</p>
                </div>

                <div className="biz-broadcast-filters">
                  <h3>Filter Options</h3>
                  <div className="biz-broadcast-filter-grid">
                    <label>
                      Last Seen
                      <select
                        value={lastSeen}
                        onChange={(e) => setLastSeen(e.target.value)}
                      >
                        {LAST_SEEN_OPTIONS.map((o) => (
                          <option key={o.value || "any"} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Created At
                      <input
                        type="date"
                        value={createdAtFilter}
                        onChange={(e) => setCreatedAtFilter(e.target.value)}
                      />
                    </label>
                    <label>
                      Opted In
                      <select
                        value={optedIn}
                        onChange={(e) => setOptedIn(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                    <label>
                      Incoming Blocked
                      <select
                        value={incomingBlocked}
                        onChange={(e) => setIncomingBlocked(e.target.value)}
                      >
                        <option value="">Select an option...</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                  </div>
                  <div className="biz-broadcast-filter-actions">
                    <button
                      type="button"
                      className="biz-btn-primary"
                      onClick={applyAudienceFilter}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      className="biz-btn-outline"
                      onClick={clearFilters}
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                {loadingCustomers ? (
                  <p className="biz-panel-hint">Loading customers…</p>
                ) : (
                  <>
                    <p className="biz-broadcast-count">
                      {totalCustomers} matching · {selectedIds.size} selected
                    </p>
                    <div className="biz-table-wrap">
                      <table className="biz-table">
                        <thead>
                          <tr>
                            <th>
                              <input
                                type="checkbox"
                                checked={
                                  customers.length > 0 &&
                                  selectedIds.size === customers.length
                                }
                                onChange={toggleAllCustomers}
                                aria-label="Select all"
                              />
                            </th>
                            <th>Name</th>
                            <th>Mobile Number</th>
                            <th>Last Active</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customers.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="biz-table-empty">
                                Apply filters to load contacts
                              </td>
                            </tr>
                          ) : (
                            customers.map((c) => (
                              <tr key={c._id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedIds.has(c._id)}
                                    onChange={() => toggleCustomer(c._id)}
                                  />
                                </td>
                                <td>{c.name || "—"}</td>
                                <td>{c.waid || c.mobile || "—"}</td>
                                <td>
                                  {c.lastActive
                                    ? new Date(
                                        Number(c.lastActive) * 1000 ||
                                          c.lastActive,
                                      ).toLocaleString()
                                    : "—"}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {step === 2 && (
              <CampaignTemplatePicker
                templates={templates}
                selectedTemplate={selectedTemplate}
                onSelect={setSelectedTemplate}
                loading={loadingTemplates}
              />
            )}

            {step === 3 && (
              <div className="biz-broadcast-schedule">
                <h2>Schedule the campaign</h2>
                <p>
                  Schedule the campaign at the favorable time to hit the
                  broadcast.
                </p>
                <label className="biz-broadcast-schedule-toggle">
                  <input
                    type="checkbox"
                    checked={scheduleOn}
                    onChange={(e) => setScheduleOn(e.target.checked)}
                  />
                  Enable scheduling
                </label>
                {scheduleOn && (
                  <div className="biz-broadcast-schedule-fields">
                    <label>
                      Select Date:
                      <input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                      />
                    </label>
                    <label>
                      Select Time:
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                      />
                    </label>
                  </div>
                )}
                <div className="biz-broadcast-summary">
                  <h3>Summary</h3>
                  <ul>
                    <li>
                      <strong>Campaign:</strong> {campaignName}
                    </li>
                    <li>
                      <strong>Recipients:</strong> {selectedIds.size}
                    </li>
                    <li>
                      <strong>Template:</strong>{" "}
                      {selectedTemplate?.name || "—"}
                    </li>
                    <li>
                      <strong>Schedule:</strong>{" "}
                      {scheduleOn
                        ? `${scheduleDate} ${scheduleTime}`
                        : "Send immediately"}
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {error && <p className="biz-panel-error">{error}</p>}

            <div className="biz-broadcast-nav">
              {step > 0 && (
                <button
                  type="button"
                  className="biz-btn-outline"
                  onClick={() => setStep((s) => s - 1)}
                  disabled={submitting}
                >
                  Back
                </button>
              )}
              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  className="biz-btn-primary"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canNext()}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  className="biz-btn-primary"
                  onClick={handleLaunch}
                  disabled={submitting || !canNext()}
                >
                  {submitting ? "Launching…" : "Launch Campaign"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BroadcastCampaign;
